import { useEffect, useRef, useState } from 'react';
import type { StepProps } from '../../OBWWizard';
import { getBrief, type BriefDetail } from '../../../lib/partner-briefs-api';
import { AssessmentInProgress } from './AssessmentInProgress';

// ADR-076 Option 2 — staff fork of pa-status. POW UAT r4 fixed the SUBSCRIBER
// fork (in-flow polled outcome + gradient AssessmentInProgress panel + Done
// gated until decided) but NOT this staff fork, which stayed on the old static
// "assessment running / Done enabled" state (POW/partner UAT punchlist item 9,
// 1 Jul 2026). This brings the staff fork to parity with the subscriber fork
// (POW-1 + POW-2), §9 copy-fork:
//   • POW-1 — poll the assessment to a decided state, showing the gradient
//     AssessmentInProgress panel while it runs.
//   • POW-2 — Done stays DISABLED until the assessment is decided AND an
//     outcome status is shown, so the operator cannot complete the launch
//     before the outcome is known.
//
// The staff fork has no subscriber session, so it CANNOT call the subscriber
// GET /portal/pow/status. Instead it polls the staff-authoritative
// getBrief(applicationId) (admin-api GET /admin/partner-applications/{id}) —
// the same read the Partners drilldown uses. Outcome copy is staff-voice (the
// APPLICANT is emailed; the result lands in the Partners queue).

// "Still running" = the agent is mid-assessment. Everything else is a decided
// outcome for the launch step (mirrors BriefDetailPage's stillRunning gate).
const RUNNING = new Set<string>(['pending_validation', 'pending_reassessment']);

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 40; // ~120s ceiling for a real LLM + web-fetch assessment

type OutcomeTone = 'approved' | 'review' | 'declined' | 'error';

interface Outcome {
  tone: OutcomeTone;
  heading: string;
  body: string;
  showRationale: boolean;
}

function outcomeFor(status: string, org: string): Outcome {
  switch (status) {
    case 'active':
    case 'approved':
    case 'pending_backlink_verification':
      return {
        tone: 'approved',
        heading: 'Application approved',
        body:
          `The suitability assessment approved ${org}. Partner access is being set up, the applicant has been emailed, and the outcome is recorded in the Partners queue.`,
        showRationale: false,
      };
    case 'staff_review':
    case 'human_review_required':
    case 'pending_response':
    case 'info_requested':
      return {
        tone: 'review',
        heading: 'Sent for review',
        body:
          `${org}'s application needs a final human look. It's in the Partners queue for your team — nothing more is needed here.`,
        showRationale: false,
      };
    case 'rejected_poor_fit':
      return {
        tone: 'declined',
        heading: 'Not a fit right now',
        body:
          `Based on the assessment, ${org} doesn't look like a fit for the partner programme as it stands. The notes below explain why; the applicant has been emailed.`,
        showRationale: true,
      };
    case 'rejected_auto':
    case 'rejected':
      return {
        tone: 'declined',
        heading: 'Application not approved',
        body:
          `${org}'s application wasn't approved this time. The notes below explain the assessment's reasoning; the applicant has been emailed and can resubmit later.`,
        showRationale: true,
      };
    case 'suspended':
    case 'deleted':
      return {
        tone: 'declined',
        heading: 'Application closed',
        body: 'This application is no longer active. See the Partners queue for detail.',
        showRationale: false,
      };
    case 'agent_failed':
      return {
        tone: 'error',
        heading: 'Assessment error',
        body:
          'Something went wrong while running the assessment. It has been flagged in the Partners queue for manual review — no action is needed here.',
        showRationale: false,
      };
    default:
      return {
        tone: 'review',
        heading: 'Application submitted',
        body: `${org}'s application is on file. Track it from the Partners queue.`,
        showRationale: false,
      };
  }
}

export function PartnerStatusStep({ data, onNext }: StepProps) {
  const applicationId = (data?.applicationId as string) ?? '';
  const org = (data?.pickedOrg as string) || (data?.businessName as string) || 'the subscriber';
  const websiteUrl = (data?.websiteUrl as string | undefined) ?? '';
  const submittedStatus = (data?.status as string | undefined) ?? 'pending_validation';

  const [brief, setBrief] = useState<BriefDetail | null>(null);
  // 'polling' while the agent runs; 'decided' once terminal; 'timeout' if the
  // assessment outruns MAX_POLLS (rare — graceful fallback, not an error).
  const [phase, setPhase] = useState<'polling' | 'decided' | 'timeout'>(
    // No applicationId means the submit response didn't carry one — treat as
    // decided so we fall through to the generic "submitted" confirmation rather
    // than polling an empty id forever.
    !applicationId || !RUNNING.has(submittedStatus) ? 'decided' : 'polling',
  );
  const pollsRef = useRef(0);
  // POW-1 — the assessment's wall-clock start drives the progress bar. Use the
  // submit instant when the step carries it; otherwise anchor to first mount so
  // the elapsed clock still reads sensibly (never future/NaN).
  const startedAtRef = useRef(
    (data?.submittedAt as string | undefined) || new Date().toISOString(),
  );

  useEffect(() => {
    // If we arrived already-decided, fetch once for the outcome copy + rationale.
    if (phase === 'decided') {
      if (applicationId) {
        void getBrief(applicationId).then((b) => setBrief(b)).catch(() => {});
      }
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      if (cancelled) return;
      pollsRef.current += 1;
      try {
        const b = await getBrief(applicationId);
        if (cancelled) return;
        setBrief(b);
        if (!RUNNING.has(b.status)) {
          setPhase('decided');
          return;
        }
      } catch {
        // Transient poll failure — keep trying until the cap.
      }
      if (cancelled) return;
      if (pollsRef.current >= MAX_POLLS) {
        setPhase('timeout');
        return;
      }
      timer = setTimeout(() => void tick(), POLL_INTERVAL_MS);
    };

    void tick();
    return () => { cancelled = true; clearTimeout(timer); };
    // Intentionally run once on mount — the poll loop self-schedules.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Still assessing ----
  // POW-1 — live activity via the §9-forked AssessmentInProgress panel (gradient
  // progress bar + stage label + elapsed clock), replacing the bare static card.
  // POW-2 — Done stays DISABLED while polling: the operator cannot complete the
  // launch before an outcome is shown.
  if (phase === 'polling') {
    return (
      <div className="obw-step-content">
        <h2 className="obw-title">Assessing the application…</h2>
        <p className="obw-subtitle">
          We’re running the suitability assessment for <strong style={{ color: 'var(--text)' }}>{org}</strong> now.
          This usually takes under a minute — please keep this window open.
        </p>
        <AssessmentInProgress websiteUrl={websiteUrl} startedAt={startedAtRef.current} />
        <DoneButton disabled />
      </div>
    );
  }

  // ---- Timed out while still pending (graceful fallback) ----
  // POW-2 — no decision was reached, so Done stays disabled; the operator closes
  // the window (✕) and the outcome lands in the Partners queue / applicant email.
  if (phase === 'timeout') {
    return (
      <div className="obw-step-content">
        <h2 className="obw-title">Still assessing</h2>
        <p className="obw-subtitle">
          The assessment for <strong style={{ color: 'var(--text)' }}>{org}</strong> is taking a little
          longer than usual. The outcome will land in the <strong>Partners queue</strong> and the applicant
          is emailed automatically — you can close this window.
        </p>
        <DoneButton disabled />
      </div>
    );
  }

  // ---- Decided ----
  const renderStatus = brief?.status ?? submittedStatus;
  const outcome = outcomeFor(renderStatus, org);
  const rationale = (brief?.scoringNote ?? '').trim();

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">{outcome.heading}</h2>
      <p className="obw-subtitle">{outcome.body}</p>

      {outcome.showRationale && rationale ? (
        <div className="obw-info-box" style={{ marginTop: '16px' }}>
          <p className="obw-form-section" style={{ marginTop: 0 }}>What the assessment found</p>
          <p style={{ margin: 0, color: 'var(--text)' }}>{rationale}</p>
        </div>
      ) : null}

      <div className="obw-info-box" style={{ marginTop: '16px' }}>
        <p style={{ margin: 0 }}>
          {applicationId
            ? <>Tracked as application <strong style={{ color: 'var(--text)' }}>{applicationId}</strong> in the Partners queue.</>
            : <>Track this application from the Partners queue.</>}
        </p>
      </div>

      {/* POW-2 — only now (decided + outcome status shown) does Done enable. */}
      <DoneButton onDone={() => onNext({ provisionedApplicationId: applicationId })} />
    </div>
  );
}

/**
 * POW-2 — the final-step completion control. pa-status is the LAST step of the
 * staff POW launch, and the OBW completion contract is "the step renders its own
 * forward control; onNext on the last step fires the mount's onComplete"
 * (OBWWizard.handleStepNext → the staff mount dismisses the modal + toasts). So
 * Done lives HERE, gated by the step's poll phase: rendered disabled while
 * `polling`/`timeout` (no outcome to act on) and enabled only once an outcome is
 * shown — making it structurally impossible to complete the launch before the
 * assessment decides.
 */
function DoneButton({ disabled, onDone }: { disabled?: boolean; onDone?: () => void }) {
  return (
    <div className="portal-wizard-nav" style={{ marginTop: '24px' }}>
      <button
        type="button"
        className="obw-btn-primary"
        onClick={onDone}
        disabled={disabled}
        aria-label="Done"
      >
        Done
      </button>
    </div>
  );
}
